/// <amd-module name="Environments/EnvironmentsCommon/AadAuthorizer" />
import { getTaskAgentClient, ITaskAgentRestClient } from "DistributedTask/Client/RestClient/TaskAgent";
import { AadLoginPromptOption } from "DistributedTask/Client/Contracts/TaskAgent";

import { IProjectData } from "Tfs/Platform/Page";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IPageData, IVssPageService } from "VSS/Platform/Page";

import * as Resources from "Environments/EnvironmentsCommon/Resources";
import { DelayedFunction } from "Environments/EnvironmentsCommon/DelayedFunction";

export interface IAadAuthorizer {
    authorize(
        tenantId: string,
        redirectUri: string,
        aadLoginPromptOption: AadLoginPromptOption,
        completeCallbackPayload: string,
        completeCallbackByAuthCode: boolean,
        aadOAuthLoginUrl?: string): Promise<string>;
}

export class AadAuthorizer implements IAadAuthorizer {
    constructor(pageContext: IVssPageContext) {
        this._pageContext = pageContext;
        this._distributedTaskClient = getTaskAgentClient(pageContext);
    }

    public getAccessTokenKey(projectData: IProjectData | undefined,  sourceTenantId: string, targetTenantId: string, accessTokenKey?: any, endpointId?: any) {
        
        return new Promise( (resolve, reject) => {
            if (!accessTokenKey) {

                if(!!projectData) {
                    // let collectionUri: string = "";
                    let projectId: string = projectData.id;

                    if (typeof window === undefined) {
                        reject("");
                    }

                    let redirectUri: string = window.location.origin + projectData.url + "/_admin/_services/completecallbackbyauthcode";
                    
                    if (endpointId !== null && endpointId !== undefined) {
                        redirectUri += "?endpointId=" + endpointId + "&projectId=" + projectId;
                    }
        
                    this.authorize(sourceTenantId, redirectUri, AadLoginPromptOption.FreshLogin, "", true).then((accessToken: string) => {
                        resolve(accessToken);
                    }, (error) => {
                        reject(error);
                    });
                }
                else 
                {
                    reject("");
                }
                
            } else {
                resolve(accessTokenKey);
            }
        });

    }

    public authorize(
        tenantId: string,
        redirectUri: string,
        aadLoginPromptOption: AadLoginPromptOption,
        completeCallbackPayload: string,
        completeCallbackByAuthCode: boolean,
        aadOAuthLoginUrl?: string): Promise<string>
    {
        return new Promise<string>((resolve, reject) => {
            
            const pageData: IPageData = <IPageData>this._pageContext.getService<IVssPageService>("IVssPageService").getData();
            if (pageData.isDevfabric && isFeatureFlagEnabled(this._pageContext, "VisualStudio.Services.UseProdAzureResourcesOnDevFabric", false)) {
                return resolve("");
            }

            this._getAadOAuthLoginUrl(tenantId, redirectUri, aadLoginPromptOption, completeCallbackPayload, completeCallbackByAuthCode, aadOAuthLoginUrl).then((oAuthUrl: any) => {
                oAuthUrl = oAuthUrl.value || oAuthUrl;
                this.authWindow = window.open(oAuthUrl, "", 'width = 960, height = 600, location = true, menubar = false, toolbar = false');
    
                if (completeCallbackPayload === "") {
                    this._pollAuthWindow().then((accessTokenKey: string) => {
                        return resolve(accessTokenKey);
                    }, (error) => {
                        return reject(error);
                    });
                }
                else {
                    return resolve("");
                }
    
            }, (error: any) => {
                return reject(error);
            });
        });
    }

    private _pollAuthWindow(): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            this._monitorAuthProgress = new DelayedFunction(this, 1000, "monitorAuthProgress", () => {
                try {
                    if (!this.authWindow) {
                        reject(Resources.CouldNotAuthenticateAAD);
                        this._cleanupAuthWindow();
                    } else if (this.authWindow.closed) {
                        reject(''); // not showing any message as closing window is not an error condition
                        this._cleanupAuthWindow();
                    } else {
                        try {
                            if (this.authWindow.vstsaadoauthcompleted) {
                                if (this.authWindow.vstsaadoautherrormessage) {
                                    reject(this.authWindow.vstsaadoautherrormessage);
                                } else if (this.authWindow.vstsaadoauthaccesstokenkey) {
                                    resolve(this.authWindow.vstsaadoauthaccesstokenkey);
                                } else {
                                    reject(Resources.SpnAuthorizationFailedError);
                                }

                                this._cleanupAuthWindow();

                            } else {
                                this._monitorAuthProgress!.reset();
                            }
                        } catch (e1) {
                            this._monitorAuthProgress!.reset();
                        }
                    }
                } catch (e) {
                    reject(e);
                    this._cleanupAuthWindow();
                }
            });

            this._monitorAuthProgress.start();
        });
    }

    private _cleanupAuthWindow(): void {

        if (this.authWindow) {
            try {
                this.authWindow.close();
                this.authWindow = null;
            } catch (e) { }
        }

        if (this._monitorAuthProgress) {
            this._monitorAuthProgress.cancel();
            delete this._monitorAuthProgress;
        }
    }

    private _getAadOAuthLoginUrl(tenantId: string, redirectUri: string, aadLoginPromptOption: AadLoginPromptOption, completeCallbackPayload: string = "", completeCallbackByAuthCode: boolean = false, aadOAuthLoginUrl?: string): Promise<string> {
        // if the OAuth login URL is provided we return the value as it is, else we query the service to get the login URL
        if (!!aadOAuthLoginUrl) {
            return Promise.resolve(aadOAuthLoginUrl);
        }
        else {
            return this._distributedTaskClient.createAadOAuthRequest(tenantId, redirectUri, aadLoginPromptOption, completeCallbackPayload, completeCallbackByAuthCode);
        }
    }

    private _distributedTaskClient: ITaskAgentRestClient;
    private _monitorAuthProgress?: DelayedFunction;
    private _pageContext: IVssPageContext;
    public authWindow: any;
}
